import React, { useMemo, useRef, useState } from "react";
import { ACTIVITY_LABELS, formatChainage, mergeIntervals } from "./VisualProgressTracker";
import {
  filterMeasurementsByPeriod,
  summariseMeasurements,
  measurementSummaryCsv,
  formatQty,
  unitLabel,
} from "./measurementModel";

/**
 * ProductivityView — daily production analytics driven by progressSegments
 * + diary dates.
 *
 * Three views, all driven by the same period filter:
 *
 *   1. "Combined"     — one chart, all activities overlaid as coloured
 *                       daily-production lines on a true time axis, with an
 *                       interactive crosshair tooltip and clickable legend
 *                       chips to show/hide individual activities.
 *   2. "Per Activity" — small-multiple grid (one mini bar chart per
 *                       activity) rendered inside a single SVG so PNG export
 *                       captures the whole grid. Shared time axis across all
 *                       cells so days line up vertically for comparison.
 *   3. "Monthly"      — month-on-month stacked production. Clicking a month
 *                       isolates the whole page to that month.
 *
 * The period selector (All time / a single month / a custom date range)
 * filters every view AND the period summary, which reports the quantity
 * installed plus the start and end sections worked in that period.
 *
 * Props (unchanged — drop-in compatible with VisualProgressTracker)
 * -----
 *   asset               - the progressAssets doc currently selected
 *   segments            - progressSegments docs for this asset
 *   diariesById         - lookup of all diaries by id
 *   measurements        - progressMeasurements docs for this asset, rolled up
 *                         into the period measurement sheet (4th view)
 *   programmeActivities - unused in this rewrite, kept for prop compatibility
 */
export default function ProductivityView({
  asset,
  segments,
  diariesById,
  measurements = [],
  programmeActivities = [], // eslint-disable-line no-unused-vars
}) {
  const [view, setView] = useState("combined");
  const [hiddenActivities, setHiddenActivities] = useState(new Set());
  const [periodMode, setPeriodMode] = useState("all"); // all | month | range
  const [periodMonth, setPeriodMonth] = useState("");
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");
  const svgWrapRef = useRef(null);

  const unit = useMemo(() => unitForAsset(asset), [asset]);

  /* Every completed segment, regardless of period — the source of truth for
     the month list, the monthly chart and the stable activity colours. */
  const allRows = useMemo(
    () => buildRows({ segments, diariesById }),
    [segments, diariesById]
  );

  /* The period selector spans production *and* measurements: a month where
     only measurements were captured still has to be selectable. */
  const periodRows = useMemo(
    () => [
      ...allRows,
      ...(measurements || []).filter((m) => m.date).map((m) => ({ date: m.date })),
    ],
    [allRows, measurements]
  );

  const months = useMemo(() => monthsFromRows(periodRows), [periodRows]);
  const colours = useMemo(() => activityColours(allRows), [allRows]);

  const period = useMemo(
    () => resolvePeriod({ periodMode, periodMonth, rangeFrom, rangeTo, allRows: periodRows }),
    [periodMode, periodMonth, rangeFrom, rangeTo, periodRows]
  );

  const rows = useMemo(
    () => allRows.filter((r) => r.date >= period.from && r.date <= period.to),
    [allRows, period]
  );

  /* The measurement sheet for the selected period — every line measured
     against a section worked in this window, grouped by item. */
  const periodMeasurements = useMemo(
    () =>
      filterMeasurementsByPeriod(
        measurements,
        // "All time" must not drop a measurement dated outside the production
        // window, so it passes no bounds at all.
        period.mode === "all" ? {} : { from: period.from, to: period.to }
      ),
    [measurements, period]
  );
  const measurementSummary = useMemo(
    () => summariseMeasurements(periodMeasurements),
    [periodMeasurements]
  );

  const data = useMemo(() => buildData(rows, unit, period, colours), [rows, unit, period, colours]);
  const monthly = useMemo(() => buildMonthly(allRows, months, unit, colours), [allRows, months, unit, colours]);

  const toggleActivity = (activity) => {
    setHiddenActivities((prev) => {
      const next = new Set(prev);
      if (next.has(activity)) next.delete(activity);
      else next.add(activity);
      return next;
    });
  };

  const onPeriodSelect = (value) => {
    if (value === "all") { setPeriodMode("all"); return; }
    if (value === "range") {
      setPeriodMode("range");
      if (!rangeFrom && allRows.length) setRangeFrom(period.from);
      if (!rangeTo && allRows.length) setRangeTo(period.to);
      return;
    }
    setPeriodMode("month");
    setPeriodMonth(value.replace(/^m:/, ""));
  };

  const pickMonth = (monthKey) => {
    setPeriodMode("month");
    setPeriodMonth(monthKey);
  };

  /* ----- PNG export (active view) ----- */
  function handleExportPng() {
    const svg = svgWrapRef.current?.querySelector("svg");
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const scale = 2;
      canvas.width = svg.viewBox.baseVal.width * scale;
      canvas.height = svg.viewBox.baseVal.height * scale;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${asset?.assetName || "asset"}-${view}-${slug(period.label)}.png`;
        link.click();
        URL.revokeObjectURL(link.href);
      });
    };
    img.src = url;
  }

  /* ----- CSV export (period summary) ----- */
  function handleExportCsv() {
    const lines = [
      [`Asset`, asset?.assetName || ""],
      [`Period`, period.label],
      [],
      ["Activity", `Quantity (${unit})`, "Working days", `Avg/day (${unit})`, `Peak day (${unit})`,
        "First worked", "First section", "Last worked", "Last section", "Extent", "Sections worked"],
      ...data.activities.map((a) => [
        a.label,
        Math.round(a.total),
        a.days,
        a.avg.toFixed(1),
        Math.round(a.peak?.qty || 0),
        a.firstWorked?.date || "",
        a.firstWorked ? `${formatChainage(a.firstWorked.start)} → ${formatChainage(a.firstWorked.end)}` : "",
        a.lastWorked?.date || "",
        a.lastWorked ? `${formatChainage(a.lastWorked.start)} → ${formatChainage(a.lastWorked.end)}` : "",
        a.extent ? `${formatChainage(a.extent.start)} → ${formatChainage(a.extent.end)}` : "",
        (a.sections || []).map((s) => `${formatChainage(s.start)}-${formatChainage(s.end)}`).join(" | "),
      ]),
      [],
      ["TOTAL", Math.round(data.grandTotal), data.dates.length, data.avgRate.toFixed(1)],
    ];
    const csv = lines
      .map((r) => (r || []).map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${asset?.assetName || "asset"}-production-${slug(period.label)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  function handleExportMeasurementCsv() {
    const csv = measurementSummaryCsv(measurementSummary, {
      assetName: asset?.assetName || "",
      periodLabel: period.label,
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${asset?.assetName || "asset"}-measurements-${slug(period.label)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  if (allRows.length === 0 && measurements.length === 0) {
    return (
      <div style={styles.wrapper}>
        <div style={styles.emptyCard}>
          <span style={{ fontSize: 32 }}>📊</span>
          <p style={{ margin: "8px 0 0", maxWidth: 420, textAlign: "center" }}>
            No completed diary entries yet. Capture progress in Daily Diaries
            and production charts will start drawing here.
          </p>
        </div>
      </div>
    );
  }

  const selectValue =
    periodMode === "all" ? "all" : periodMode === "range" ? "range" : `m:${period.monthKey}`;

  return (
    <div style={styles.wrapper}>
      {/* ── Period filter ── */}
      <div style={styles.periodBar}>
        <span style={styles.periodLabel}>Period</span>
        <select
          value={selectValue}
          onChange={(e) => onPeriodSelect(e.target.value)}
          style={styles.select}
        >
          <option value="all">All time ({months.length} month{months.length === 1 ? "" : "s"})</option>
          <optgroup label="Month">
            {months.map((m) => (
              <option key={m} value={`m:${m}`}>{monthLabel(m)}</option>
            ))}
          </optgroup>
          <option value="range">Custom range…</option>
        </select>

        {periodMode === "month" && months.length > 1 && (
          <span style={styles.stepper}>
            <button
              style={styles.stepBtn}
              disabled={months.indexOf(period.monthKey) <= 0}
              onClick={() => pickMonth(months[months.indexOf(period.monthKey) - 1])}
              title="Previous month"
            >‹</button>
            <button
              style={styles.stepBtn}
              disabled={months.indexOf(period.monthKey) >= months.length - 1}
              onClick={() => pickMonth(months[months.indexOf(period.monthKey) + 1])}
              title="Next month"
            >›</button>
          </span>
        )}

        {periodMode === "range" && (
          <>
            <input type="date" value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)} style={styles.dateInput} />
            <span style={styles.rangeDash}>→</span>
            <input type="date" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)} style={styles.dateInput} />
          </>
        )}

        <span style={styles.periodBadge}>{period.label}</span>
        {periodMode !== "all" && (
          <button style={styles.clearBtn} onClick={() => setPeriodMode("all")}>Clear</button>
        )}
      </div>

      {/* ── Period summary: quantity installed + start / end sections ── */}
      <PeriodSummary data={data} period={period} onExportCsv={handleExportCsv} />

      {/* ── Toolbar ── */}
      <div style={styles.toolbar}>
        <div style={styles.chartTabs}>
          <button
            style={{ ...styles.tab, ...(view === "combined" ? styles.tabActive : {}) }}
            onClick={() => setView("combined")}
          >
            📈 Combined
          </button>
          <button
            style={{ ...styles.tab, ...(view === "per_activity" ? styles.tabActive : {}) }}
            onClick={() => setView("per_activity")}
          >
            📐 Per Activity
          </button>
          <button
            style={{ ...styles.tab, ...(view === "monthly" ? styles.tabActive : {}) }}
            onClick={() => setView("monthly")}
          >
            📅 Monthly
          </button>
          <button
            style={{ ...styles.tab, ...(view === "measurements" ? styles.tabActive : {}) }}
            onClick={() => setView("measurements")}
            title="Total quantities measured against the sections worked in this period"
          >
            📋 Measurement Sheet
            {measurementSummary.items.length > 0 && (
              <span style={styles.tabCount}>{measurementSummary.items.length}</span>
            )}
          </button>
        </div>
        {view === "measurements" ? (
          <button
            style={styles.exportButton}
            onClick={handleExportMeasurementCsv}
            disabled={measurementSummary.items.length === 0}
          >
            📄 Export CSV
          </button>
        ) : (
          <button style={styles.exportButton} onClick={handleExportPng}>
            📥 Export PNG
          </button>
        )}
      </div>

      {/* ── Legend chips (toggle visibility on the overlaid charts) ── */}
      {(view === "combined" || view === "monthly") && data.activities.length > 0 && (
        <div style={styles.legendRow}>
          {(view === "monthly" ? monthly.activities : data.activities).map((a) => {
            const hidden = hiddenActivities.has(a.activity);
            return (
              <button
                key={a.activity}
                onClick={() => toggleActivity(a.activity)}
                style={{
                  ...styles.legendChip,
                  borderColor: a.colour,
                  opacity: hidden ? 0.35 : 1,
                  textDecoration: hidden ? "line-through" : "none",
                }}
                title={hidden ? "Click to show" : "Click to hide"}
              >
                <span style={{ ...styles.legendDot, background: a.colour }} />
                {a.label}
                <span style={styles.legendTotal}>
                  {Math.round(a.total).toLocaleString("en-ZA")} {unit}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* ── Chart host ── */}
      <div ref={svgWrapRef} style={styles.chartHost}>
        {view === "measurements" ? (
          <MeasurementSheetSummary
            summary={measurementSummary}
            period={period}
            totalMeasurements={measurements.length}
          />
        ) : view === "monthly" ? (
          <MonthlyChart
            monthly={monthly}
            hiddenActivities={hiddenActivities}
            activeMonth={periodMode === "month" ? period.monthKey : null}
            onPickMonth={pickMonth}
          />
        ) : data.activities.length === 0 ? (
          <div style={styles.emptyInline}>
            No production recorded in {period.label}.
          </div>
        ) : view === "combined" ? (
          <CombinedChart data={data} hiddenActivities={hiddenActivities} />
        ) : (
          <PerActivityGrid data={data} />
        )}
      </div>

      {/* ── Footer stats ── */}
      <div style={styles.footerStats}>
        <Stat label="Total Production" value={`${Math.round(data.grandTotal).toLocaleString("en-ZA")} ${data.unit}`} helper={period.label} />
        <Stat label="Activities" value={data.activities.length} />
        <Stat label="Working Days" value={data.dates.length} />
        <Stat label="Average / Day" value={`${data.avgRate.toFixed(0)} ${data.unit}/day`} />
        <Stat
          label="Peak Day"
          value={data.peakDay ? `${Math.round(data.peakDay.total)} ${data.unit}` : "—"}
          helper={data.peakDay ? formatShortDate(data.peakDay.date) : ""}
        />
        <Stat
          label="Sections Worked"
          value={data.extent ? `${formatChainage(data.extent.start)} → ${formatChainage(data.extent.end)}` : "—"}
          helper={data.sections.length ? `${data.sections.length} run${data.sections.length === 1 ? "" : "s"}` : ""}
        />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* MEASUREMENT SHEET — total quantities measured in the period          */
/*                                                                      */
/* Production charts answer "how much pipe went in". This answers the    */
/* other half: how much rockfill, bidim and stone went in with it, at    */
/* the properties measured against each section.                        */
/* ------------------------------------------------------------------ */

export function MeasurementSheetSummary({ summary, period, totalMeasurements }) {
  const [openItem, setOpenItem] = useState(null);

  if (summary.items.length === 0) {
    return (
      <div style={styles.emptyInline}>
        {totalMeasurements === 0
          ? "No measurements recorded yet. Open a section in the trench view and use Daily Measurements to capture quantities against it."
          : `No measurements fall in ${period.label}.`}
      </div>
    );
  }

  return (
    <div style={styles.sheet}>
      {/* Headline totals, one per unit in play */}
      <div style={styles.sheetTotals}>
        {Object.entries(summary.totalsByUnit).map(([unit, total]) => (
          <div key={unit} style={styles.sheetTotalCard}>
            <span style={styles.sheetTotalLabel}>Total {unitLabel(unit)}</span>
            <strong style={styles.sheetTotalValue}>{formatQty(total, unit)}</strong>
          </div>
        ))}
        <div style={styles.sheetTotalCard}>
          <span style={styles.sheetTotalLabel}>Lines measured</span>
          <strong style={styles.sheetTotalValue}>{summary.entries}</strong>
          <span style={styles.sheetTotalSub}>
            over {summary.days} day{summary.days === 1 ? "" : "s"}
          </span>
        </div>
      </div>

      <div style={styles.tableScroll}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Item</th>
              <th style={{ ...styles.th, textAlign: "right" }}>Quantity</th>
              <th style={{ ...styles.th, textAlign: "right" }}>Lines</th>
              <th style={{ ...styles.th, textAlign: "right" }}>Days</th>
              <th style={{ ...styles.th, textAlign: "right" }}>Sections</th>
              <th style={styles.th}>Extent measured</th>
              <th style={styles.th}>Activities</th>
            </tr>
          </thead>
          <tbody>
            {summary.groups.map((group) => (
              <React.Fragment key={group.group}>
                <tr>
                  <td colSpan={7} style={styles.sheetGroupRow}>{group.group}</td>
                </tr>
                {group.items.map((item) => {
                  const open = openItem === item.key;
                  return (
                    <React.Fragment key={item.key}>
                      <tr
                        style={styles.sheetRow}
                        onClick={() => setOpenItem(open ? null : item.key)}
                        title="Click to show every line behind this total"
                      >
                        <td style={styles.td}>
                          <span style={styles.sheetCaret}>{open ? "▾" : "▸"}</span>
                          <strong>{item.label}</strong>
                        </td>
                        <td style={{ ...styles.td, textAlign: "right", fontWeight: 800 }}>
                          {formatQty(item.total, item.unit)} {unitLabel(item.unit)}
                        </td>
                        <td style={{ ...styles.td, textAlign: "right" }}>{item.entries}</td>
                        <td style={{ ...styles.td, textAlign: "right" }}>{item.days}</td>
                        <td style={{ ...styles.td, textAlign: "right" }}>{item.sections}</td>
                        <td style={styles.td}>
                          {item.chainageStart === null ? "—" : (
                            <span style={styles.chainageCell}>
                              {formatChainage(item.chainageStart)} → {formatChainage(item.chainageEnd)}
                            </span>
                          )}
                        </td>
                        <td style={styles.td}>
                          <span style={styles.runWrap}>
                            {item.activities.map((a) => (
                              <span key={a} style={styles.sheetActivityChip}>
                                {ACTIVITY_LABELS[a] || a}
                              </span>
                            ))}
                          </span>
                        </td>
                      </tr>

                      {/* Every line behind the total — the audit trail a claim
                          has to stand on. */}
                      {open && (
                        <tr>
                          <td colSpan={7} style={styles.sheetDetailCell}>
                            <table style={styles.sheetDetailTable}>
                              <thead>
                                <tr>
                                  {["Date", "Section", "Length", "Properties", "Workings", "Quantity"].map((h) => (
                                    <th key={h} style={styles.sheetDetailTh}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {item.rows.map((r) => (
                                  <tr key={r.id}>
                                    <td style={styles.sheetDetailTd}>{r.date || "—"}</td>
                                    <td style={styles.sheetDetailTd}>
                                      {formatChainage(r.chainageStart)} → {formatChainage(r.chainageEnd)}
                                      {r.excavationCondition ? ` · ${r.excavationCondition}` : ""}
                                      {r.diaryNo ? ` · #${r.diaryNo}` : ""}
                                    </td>
                                    <td style={{ ...styles.sheetDetailTd, textAlign: "right" }}>
                                      {Number(r.measuredLengthM || 0).toLocaleString("en-ZA")} m
                                    </td>
                                    <td style={styles.sheetDetailTd}>
                                      {[
                                        r.widthM ? `W ${r.widthM} m` : "",
                                        r.thicknessMm ? `T ${r.thicknessMm} mm` : "",
                                        r.layers && Number(r.layers) !== 1 ? `× ${r.layers}` : "",
                                      ].filter(Boolean).join(" · ") || "—"}
                                    </td>
                                    <td style={styles.sheetDetailTd}>{r.workings || "—"}</td>
                                    <td style={{ ...styles.sheetDetailTd, textAlign: "right", fontWeight: 800 }}>
                                      {formatQty(r.qty, r.unit)} {unitLabel(r.unit)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <p style={styles.sheetFoot}>
        Quantities are calculated from each section's length and the properties
        measured against it — change a property on the line and the total follows.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* PERIOD SUMMARY — quantity installed + start / end sections          */
/* ------------------------------------------------------------------ */

function PeriodSummary({ data, period, onExportCsv }) {
  if (data.activities.length === 0) {
    return (
      <div style={styles.summaryCard}>
        <div style={styles.summaryHead}>
          <h3 style={styles.summaryTitle}>Period summary — {period.label}</h3>
        </div>
        <div style={styles.emptyInline}>No completed production in this period.</div>
      </div>
    );
  }

  const lead = data.activities[0];

  return (
    <div style={styles.summaryCard}>
      <div style={styles.summaryHead}>
        <h3 style={styles.summaryTitle}>Period summary — {period.label}</h3>
        <button style={styles.csvButton} onClick={onExportCsv}>📄 Export CSV</button>
      </div>

      <p style={styles.summaryHeadline}>
        <strong style={{ fontSize: 22 }}>
          {Math.round(data.grandTotal).toLocaleString("en-ZA")} {data.unit}
        </strong>{" "}
        across {data.activities.length} activit{data.activities.length === 1 ? "y" : "ies"} over{" "}
        {data.dates.length} working day{data.dates.length === 1 ? "" : "s"}
        {lead ? (
          <> — leading activity <strong>{lead.label}</strong> at{" "}
            {Math.round(lead.total).toLocaleString("en-ZA")} {data.unit}
            {lead.extent ? <> from {formatChainage(lead.extent.start)} to {formatChainage(lead.extent.end)}</> : null}.
          </>
        ) : null}
      </p>

      <div style={styles.tableScroll}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Activity</th>
              <th style={{ ...styles.th, textAlign: "right" }}>Installed</th>
              <th style={{ ...styles.th, textAlign: "right" }}>Days</th>
              <th style={{ ...styles.th, textAlign: "right" }}>Avg/day</th>
              <th style={styles.th}>Start section</th>
              <th style={styles.th}>End section</th>
              <th style={styles.th}>Sections worked</th>
            </tr>
          </thead>
          <tbody>
            {data.activities.map((a) => (
              <tr key={a.activity}>
                <td style={styles.td}>
                  <span style={{ ...styles.legendDot, background: a.colour, marginRight: 7 }} />
                  <strong>{a.label}</strong>
                </td>
                <td style={{ ...styles.td, textAlign: "right", fontWeight: 800 }}>
                  {Math.round(a.total).toLocaleString("en-ZA")} {data.unit}
                </td>
                <td style={{ ...styles.td, textAlign: "right" }}>{a.days}</td>
                <td style={{ ...styles.td, textAlign: "right" }}>
                  {a.avg.toFixed(0)} {data.unit}
                </td>
                <td style={styles.td}>
                  {a.firstWorked ? (
                    <>
                      <span style={styles.chainageCell}>
                        {formatChainage(a.firstWorked.start)} → {formatChainage(a.firstWorked.end)}
                      </span>
                      <span style={styles.cellHelper}>{formatShortDate(a.firstWorked.date)}</span>
                    </>
                  ) : "—"}
                </td>
                <td style={styles.td}>
                  {a.lastWorked ? (
                    <>
                      <span style={styles.chainageCell}>
                        {formatChainage(a.lastWorked.start)} → {formatChainage(a.lastWorked.end)}
                      </span>
                      <span style={styles.cellHelper}>{formatShortDate(a.lastWorked.date)}</span>
                    </>
                  ) : "—"}
                </td>
                <td style={styles.td}>
                  <span style={styles.runWrap}>
                    {(a.sections || []).map((s) => (
                      <span key={`${s.start}-${s.end}`} style={{ ...styles.runChip, borderColor: a.colour }}>
                        {formatChainage(s.start)} → {formatChainage(s.end)}
                        <em style={styles.runLen}>{Math.round(s.end - s.start).toLocaleString("en-ZA")} {data.unit}</em>
                      </span>
                    ))}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td style={styles.tfootTd}>Total</td>
              <td style={{ ...styles.tfootTd, textAlign: "right" }}>
                {Math.round(data.grandTotal).toLocaleString("en-ZA")} {data.unit}
              </td>
              <td style={{ ...styles.tfootTd, textAlign: "right" }}>{data.dates.length}</td>
              <td style={{ ...styles.tfootTd, textAlign: "right" }}>
                {data.avgRate.toFixed(0)} {data.unit}
              </td>
              <td style={styles.tfootTd} colSpan={3}>
                {data.extent
                  ? `Overall ${formatChainage(data.extent.start)} → ${formatChainage(data.extent.end)}`
                  : "—"}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Data preparation                                                    */
/* ------------------------------------------------------------------ */

export function buildRows({ segments, diariesById }) {
  return (segments || [])
    .filter((s) => s.status === "completed")
    .map((s) => {
      const date = s.diaryDate || diariesById?.[s.diaryId]?.date || "";
      const start = Number(s.chainageStart);
      const end = Number(s.chainageEnd);
      const qty = Math.max(0, end - start);
      return { date, qty, start, end, activity: s.activity || "unspecified" };
    })
    .filter((r) => r.date && r.qty > 0);
}

export function monthsFromRows(rows) {
  return [...new Set(rows.map((r) => r.date.slice(0, 7)))].sort();
}

/* Colours are assigned once over ALL rows so an activity keeps the same
   colour no matter which period is isolated. */
function activityColours(rows) {
  const totals = {};
  rows.forEach((r) => { totals[r.activity] = (totals[r.activity] || 0) + r.qty; });
  const keys = Object.keys(totals).sort((a, b) => totals[b] - totals[a]);
  const map = {};
  keys.forEach((k, i) => { map[k] = ACTIVITY_PALETTE[i % ACTIVITY_PALETTE.length]; });
  return map;
}

export function resolvePeriod({ periodMode, periodMonth, rangeFrom, rangeTo, allRows }) {
  const dates = allRows.map((r) => r.date).sort();
  const firstDate = dates[0] || "";
  const lastDate = dates[dates.length - 1] || "";
  const months = monthsFromRows(allRows);

  if (periodMode === "month") {
    const key = months.includes(periodMonth) ? periodMonth : months[months.length - 1] || "";
    if (!key) return { mode: "all", from: firstDate, to: lastDate, label: "All time", monthKey: "" };
    return {
      mode: "month",
      monthKey: key,
      from: `${key}-01`,
      to: lastDayOfMonth(key),
      domainFrom: `${key}-01`,
      domainTo: lastDayOfMonth(key),
      label: monthLabel(key),
    };
  }

  if (periodMode === "range") {
    const from = rangeFrom || firstDate;
    const to = rangeTo || lastDate;
    return {
      mode: "range",
      monthKey: "",
      from: from <= to ? from : to,
      to: from <= to ? to : from,
      domainFrom: from <= to ? from : to,
      domainTo: from <= to ? to : from,
      label: `${formatShortDate(from)} → ${formatShortDate(to)}`,
    };
  }

  return { mode: "all", monthKey: "", from: firstDate, to: lastDate, label: "All time" };
}

export function buildData(rows, unit, period, colours) {
  if (rows.length === 0) {
    return {
      activities: [], dates: [], dateDomain: [], dateIndex: {}, unit,
      grandTotal: 0, avgRate: 0, peakDay: null, sections: [], extent: null,
    };
  }

  // Continuous date domain so the X axis is honest about time — gaps between
  // production days render as gaps. When a period is isolated the domain
  // spans the whole period, so idle days at either end stay visible.
  const productionDates = [...new Set(rows.map((r) => r.date))].sort();
  const firstProd = productionDates[0];
  const lastProd = productionDates[productionDates.length - 1];
  const domainFrom = period?.domainFrom && period.domainFrom < firstProd ? period.domainFrom : firstProd;
  const domainTo = period?.domainTo && period.domainTo > lastProd ? period.domainTo : lastProd;
  const dateDomain = eachDate(domainFrom, domainTo);
  const dateIndex = {};
  dateDomain.forEach((d, i) => { dateIndex[d] = i; });

  // Per-activity daily map, ordered biggest total first
  const totals = {};
  rows.forEach((r) => { totals[r.activity] = (totals[r.activity] || 0) + r.qty; });
  const activityKeys = Object.keys(totals).sort((a, b) => totals[b] - totals[a]);

  const activities = activityKeys.map((activity, i) => {
    const mine = rows.filter((r) => r.activity === activity);
    const daily = {};
    mine.forEach((r) => { daily[r.date] = (daily[r.date] || 0) + r.qty; });
    const points = Object.keys(daily).sort().map((date) => ({ date, qty: daily[date] }));
    const total = totals[activity] || 0;
    const days = points.length;
    const peak = points.reduce((m, p) => (p.qty > m.qty ? p : m), points[0] || { qty: 0 });
    const sections = mergeIntervals(mine.map((r) => ({ start: r.start, end: r.end })));

    return {
      activity,
      label: ACTIVITY_LABELS[activity] || activity,
      colour: colours?.[activity] || ACTIVITY_PALETTE[i % ACTIVITY_PALETTE.length],
      daily,
      points,
      total,
      days,
      avg: days > 0 ? total / days : 0,
      peak,
      sections,
      extent: extentOf(mine),
      firstWorked: workedOn(mine, points[0]?.date),
      lastWorked: workedOn(mine, points[points.length - 1]?.date),
    };
  });

  // Daily grand totals (for peak day stat)
  const dailyTotal = {};
  rows.forEach((r) => { dailyTotal[r.date] = (dailyTotal[r.date] || 0) + r.qty; });
  const peakDayDate = Object.keys(dailyTotal).reduce(
    (best, d) => (dailyTotal[d] > (dailyTotal[best] || 0) ? d : best),
    productionDates[0]
  );

  const grandTotal = rows.reduce((s, r) => s + r.qty, 0);

  return {
    activities,
    dates: productionDates,
    dateDomain,
    dateIndex,
    unit,
    grandTotal,
    avgRate: productionDates.length > 0 ? grandTotal / productionDates.length : 0,
    peakDay: { date: peakDayDate, total: dailyTotal[peakDayDate] || 0 },
    sections: mergeIntervals(rows.map((r) => ({ start: r.start, end: r.end }))),
    extent: extentOf(rows),
  };
}

/* Chainage covered by a set of rows: lowest start → highest end. */
export function extentOf(rows) {
  if (!rows || rows.length === 0) return null;
  return {
    start: Math.min(...rows.map((r) => r.start)),
    end: Math.max(...rows.map((r) => r.end)),
  };
}

/* The chainage band worked on one specific day. */
function workedOn(rows, date) {
  if (!date) return null;
  const onDay = rows.filter((r) => r.date === date);
  if (onDay.length === 0) return null;
  const ext = extentOf(onDay);
  return { date, start: ext.start, end: ext.end, qty: onDay.reduce((s, r) => s + r.qty, 0) };
}

export function buildMonthly(rows, months, unit, colours) {
  const totals = {};
  rows.forEach((r) => { totals[r.activity] = (totals[r.activity] || 0) + r.qty; });
  const keys = Object.keys(totals).sort((a, b) => totals[b] - totals[a]);
  const activities = keys.map((activity, i) => ({
    activity,
    label: ACTIVITY_LABELS[activity] || activity,
    colour: colours?.[activity] || ACTIVITY_PALETTE[i % ACTIVITY_PALETTE.length],
    total: totals[activity] || 0,
  }));

  const buckets = months.map((key) => {
    const mine = rows.filter((r) => r.date.slice(0, 7) === key);
    const byActivity = {};
    mine.forEach((r) => { byActivity[r.activity] = (byActivity[r.activity] || 0) + r.qty; });
    return {
      key,
      label: monthShortLabel(key),
      total: mine.reduce((s, r) => s + r.qty, 0),
      byActivity,
      days: new Set(mine.map((r) => r.date)).size,
      extent: extentOf(mine),
    };
  });

  return { months: buckets, activities, unit };
}

function unitForAsset(asset) {
  const m = asset?.assetType;
  if (m === "earthworks") return "m³";
  return "m";
}

function lastDayOfMonth(key) {
  const [y, m] = key.split("-").map(Number);
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}

function monthLabel(key) {
  const d = new Date(`${key}-01T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return key;
  return d.toLocaleDateString("en-ZA", { month: "long", year: "numeric", timeZone: "UTC" });
}

function monthShortLabel(key) {
  const d = new Date(`${key}-01T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return key;
  return d.toLocaleDateString("en-ZA", { month: "short", year: "2-digit", timeZone: "UTC" });
}

function slug(text) {
  return String(text || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function eachDate(startIso, finishIso) {
  const out = [];
  const start = new Date(startIso);
  const finish = new Date(finishIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(finish.getTime())) return out;
  const cur = new Date(start);
  while (cur <= finish) {
    out.push(cur.toISOString().slice(0, 10));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* COMBINED CHART — all activities overlaid, daily qty vs time         */
/* ------------------------------------------------------------------ */

const CW = 1000;
const CH = 460;
const CPAD = { top: 34, right: 30, bottom: 56, left: 64 };

function CombinedChart({ data, hiddenActivities }) {
  const [hover, setHover] = useState(null); // { idx }
  const hostRef = useRef(null);

  const visible = data.activities.filter((a) => !hiddenActivities.has(a.activity));
  const innerW = CW - CPAD.left - CPAD.right;
  const innerH = CH - CPAD.top - CPAD.bottom;
  const n = data.dateDomain.length;

  const maxQ = Math.max(
    1,
    ...visible.flatMap((a) => a.points.map((p) => p.qty))
  );

  const xFor = (date) => {
    const i = data.dateIndex[date] ?? 0;
    if (n <= 1) return CPAD.left + innerW / 2;
    return CPAD.left + (i / (n - 1)) * innerW;
  };
  const yFor = (q) => CPAD.top + innerH - (q / maxQ) * innerH;

  /* Crosshair: map mouse x to nearest domain date */
  const onMove = (e) => {
    const host = hostRef.current;
    if (!host) return;
    const rect = host.getBoundingClientRect();
    const relX = ((e.clientX - rect.left) / rect.width) * CW;
    if (relX < CPAD.left || relX > CW - CPAD.right) { setHover(null); return; }
    const frac = (relX - CPAD.left) / innerW;
    const idx = Math.round(frac * (n - 1));
    setHover({ idx: Math.max(0, Math.min(n - 1, idx)) });
  };

  const hoverDate = hover != null ? data.dateDomain[hover.idx] : null;
  const hoverRows = hoverDate
    ? visible
        .map((a) => ({ label: a.label, colour: a.colour, qty: a.daily[hoverDate] || 0 }))
        .filter((r) => r.qty > 0)
    : [];

  return (
    <div ref={hostRef} style={{ position: "relative", width: "100%" }}
      onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
      <svg viewBox={`0 0 ${CW} ${CH}`} style={{ width: "100%", display: "block" }}>
        <rect width={CW} height={CH} fill="#ffffff" />
        <text x={CPAD.left} y={22} fontSize="14" fontWeight="800" fill="#0f172a">
          Daily Production by Activity
        </text>
        <text
          x={CPAD.left - 46}
          y={CPAD.top + innerH / 2}
          fontSize="11" fill="#64748b" fontWeight="700" textAnchor="middle"
          transform={`rotate(-90, ${CPAD.left - 46}, ${CPAD.top + innerH / 2})`}
        >
          {data.unit} / day
        </text>

        {/* Y grid */}
        {Array.from({ length: 6 }).map((_, i) => {
          const y = CPAD.top + (i / 5) * innerH;
          const val = maxQ * (1 - i / 5);
          return (
            <g key={i}>
              <line x1={CPAD.left} y1={y} x2={CW - CPAD.right} y2={y} stroke="#eef2f7" strokeWidth="1" />
              <text x={CPAD.left - 8} y={y + 4} fontSize="10" fill="#64748b" textAnchor="end" fontWeight="600">
                {Math.round(val).toLocaleString("en-ZA")}
              </text>
            </g>
          );
        })}

        {/* Week boundary gridlines (Mondays) */}
        {data.dateDomain.map((d) => {
          const day = new Date(d).getDay();
          if (day !== 1) return null;
          const x = xFor(d);
          return <line key={`wk-${d}`} x1={x} y1={CPAD.top} x2={x} y2={CH - CPAD.bottom} stroke="#f1f5f9" strokeWidth="1" />;
        })}

        {/* X axis labels */}
        <XAxisLabels dateDomain={data.dateDomain} xFor={xFor} baseY={CH - CPAD.bottom} />

        {/* One line per visible activity */}
        {visible.map((a) => {
          const pts = a.points.map((p) => [xFor(p.date), yFor(p.qty)]);
          const path = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
          return (
            <g key={a.activity}>
              {/* soft glow under the line */}
              <path d={path} fill="none" stroke={a.colour} strokeWidth="6" opacity="0.12" strokeLinejoin="round" strokeLinecap="round" />
              <path d={path} fill="none" stroke={a.colour} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
              {a.points.map((p) => (
                <circle
                  key={p.date}
                  cx={xFor(p.date)} cy={yFor(p.qty)} r="3.4"
                  fill="#ffffff" stroke={a.colour} strokeWidth="2"
                >
                  <title>{`${a.label} — ${p.date}: ${Math.round(p.qty)} ${data.unit}`}</title>
                </circle>
              ))}
            </g>
          );
        })}

        {/* Crosshair */}
        {hoverDate && (
          <line
            x1={xFor(hoverDate)} y1={CPAD.top}
            x2={xFor(hoverDate)} y2={CH - CPAD.bottom}
            stroke="#94a3b8" strokeWidth="1" strokeDasharray="4 3"
          />
        )}
      </svg>

      {/* Floating tooltip */}
      {hoverDate && hoverRows.length > 0 && (
        <div
          style={{
            ...styles.tooltip,
            left: `${(xFor(hoverDate) / CW) * 100}%`,
            transform: xFor(hoverDate) > CW * 0.65 ? "translate(-105%, 0)" : "translate(8px, 0)",
          }}
        >
          <div style={styles.tooltipDate}>{formatLongDate(hoverDate)}</div>
          {hoverRows.map((r) => (
            <div key={r.label} style={styles.tooltipRow}>
              <span style={{ ...styles.legendDot, background: r.colour }} />
              <span style={{ flex: 1 }}>{r.label}</span>
              <strong>{Math.round(r.qty)} {data.unit}</strong>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* MONTHLY CHART — stacked production per month, click to isolate      */
/* ------------------------------------------------------------------ */

const MW = 1000;
const MH = 430;
const MPAD = { top: 40, right: 24, bottom: 62, left: 64 };

function MonthlyChart({ monthly, hiddenActivities, activeMonth, onPickMonth }) {
  const visible = monthly.activities.filter((a) => !hiddenActivities.has(a.activity));
  const innerW = MW - MPAD.left - MPAD.right;
  const innerH = MH - MPAD.top - MPAD.bottom;
  const n = monthly.months.length;

  const visibleTotal = (m) =>
    visible.reduce((s, a) => s + (m.byActivity[a.activity] || 0), 0);

  const maxQ = Math.max(1, ...monthly.months.map(visibleTotal));
  const slot = innerW / Math.max(n, 1);
  const barW = Math.min(76, slot * 0.62);

  return (
    <svg viewBox={`0 0 ${MW} ${MH}`} style={{ width: "100%", display: "block" }}>
      <rect width={MW} height={MH} fill="#ffffff" />
      <text x={MPAD.left} y={24} fontSize="14" fontWeight="800" fill="#0f172a">
        Production per Month — click a month to isolate it
      </text>
      <text
        x={MPAD.left - 46}
        y={MPAD.top + innerH / 2}
        fontSize="11" fill="#64748b" fontWeight="700" textAnchor="middle"
        transform={`rotate(-90, ${MPAD.left - 46}, ${MPAD.top + innerH / 2})`}
      >
        {monthly.unit} / month
      </text>

      {/* Y grid */}
      {Array.from({ length: 6 }).map((_, i) => {
        const y = MPAD.top + (i / 5) * innerH;
        const val = maxQ * (1 - i / 5);
        return (
          <g key={i}>
            <line x1={MPAD.left} y1={y} x2={MW - MPAD.right} y2={y} stroke="#eef2f7" strokeWidth="1" />
            <text x={MPAD.left - 8} y={y + 4} fontSize="10" fill="#64748b" textAnchor="end" fontWeight="600">
              {Math.round(val).toLocaleString("en-ZA")}
            </text>
          </g>
        );
      })}

      {monthly.months.map((m, i) => {
        const cx = MPAD.left + slot * i + slot / 2;
        const total = visibleTotal(m);
        let cursor = MPAD.top + innerH;
        const isActive = activeMonth === m.key;

        return (
          <g key={m.key} onClick={() => onPickMonth(m.key)} style={{ cursor: "pointer" }}>
            {/* Click / highlight band */}
            <rect
              x={cx - slot / 2} y={MPAD.top} width={slot} height={innerH}
              fill={isActive ? "#e0f2fe" : "transparent"}
              opacity={isActive ? 0.7 : 1}
              rx="8"
            />
            {visible.map((a) => {
              const q = m.byActivity[a.activity] || 0;
              if (q <= 0) return null;
              const h = (q / maxQ) * innerH;
              cursor -= h;
              return (
                <rect
                  key={a.activity}
                  x={cx - barW / 2} y={cursor}
                  width={barW} height={Math.max(h, 1)}
                  fill={a.colour} opacity="0.9"
                >
                  <title>{`${monthLabel(m.key)} — ${a.label}: ${Math.round(q).toLocaleString("en-ZA")} ${monthly.unit}`}</title>
                </rect>
              );
            })}

            {/* Total label above the stack */}
            {total > 0 && (
              <text
                x={cx} y={MPAD.top + innerH - (total / maxQ) * innerH - 7}
                fontSize="11" fontWeight="800" fill="#0f172a" textAnchor="middle"
              >
                {Math.round(total).toLocaleString("en-ZA")}
              </text>
            )}

            {/* Month label + extent */}
            <text
              x={cx} y={MH - MPAD.bottom + 18}
              fontSize="11" fontWeight={isActive ? 800 : 700}
              fill={isActive ? "#0369a1" : "#475569"} textAnchor="middle"
            >
              {m.label}
            </text>
            <text x={cx} y={MH - MPAD.bottom + 33} fontSize="9" fill="#94a3b8" textAnchor="middle" fontWeight="600">
              {m.days} day{m.days === 1 ? "" : "s"}
            </text>
            {m.extent && (
              <text x={cx} y={MH - MPAD.bottom + 46} fontSize="9" fill="#94a3b8" textAnchor="middle" fontWeight="600">
                {formatChainage(m.extent.start)}→{formatChainage(m.extent.end)}
              </text>
            )}
          </g>
        );
      })}

      <line
        x1={MPAD.left} y1={MPAD.top + innerH}
        x2={MW - MPAD.right} y2={MPAD.top + innerH}
        stroke="#0f172a" strokeWidth="1.2"
      />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* PER-ACTIVITY GRID — small multiples, one bar chart per activity     */
/* Rendered in ONE SVG (2 columns) so PNG export captures the grid.    */
/* ------------------------------------------------------------------ */

const CELL_W = 488;
const CELL_H = 210;
const CELL_GAP = 18;
const CELL_PAD = { top: 44, right: 14, bottom: 30, left: 44 };

function PerActivityGrid({ data }) {
  const cols = 2;
  const rows = Math.ceil(data.activities.length / cols);
  const gridW = cols * CELL_W + (cols - 1) * CELL_GAP + 8;
  const gridH = rows * CELL_H + (rows - 1) * CELL_GAP + 8;

  const n = data.dateDomain.length;

  return (
    <svg viewBox={`0 0 ${gridW} ${gridH}`} style={{ width: "100%", display: "block" }}>
      <rect width={gridW} height={gridH} fill="#ffffff" />
      {data.activities.map((a, i) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        const ox = 4 + col * (CELL_W + CELL_GAP);
        const oy = 4 + row * (CELL_H + CELL_GAP);
        const innerW = CELL_W - CELL_PAD.left - CELL_PAD.right;
        const innerH = CELL_H - CELL_PAD.top - CELL_PAD.bottom;
        const maxQ = Math.max(1, ...a.points.map((p) => p.qty));

        const xFor = (date) => {
          const di = data.dateIndex[date] ?? 0;
          if (n <= 1) return ox + CELL_PAD.left + innerW / 2;
          return ox + CELL_PAD.left + (di / (n - 1)) * innerW;
        };
        const barW = Math.max(2, Math.min(14, innerW / Math.max(n, 1) * 0.7));

        return (
          <g key={a.activity}>
            {/* Cell frame */}
            <rect
              x={ox} y={oy} width={CELL_W} height={CELL_H}
              fill="#fbfcfe" stroke="#e2e8f0" strokeWidth="1" rx="12"
            />
            {/* Header */}
            <circle cx={ox + 18} cy={oy + 20} r="5" fill={a.colour} />
            <text x={ox + 30} y={oy + 24} fontSize="13" fontWeight="800" fill="#0f172a">
              {a.label}
            </text>
            <text x={ox + CELL_W - 14} y={oy + 24} fontSize="11" fontWeight="700" fill="#64748b" textAnchor="end">
              {Math.round(a.total).toLocaleString("en-ZA")} {data.unit} · avg {a.avg.toFixed(0)}/day · peak {Math.round(a.peak?.qty || 0)}
            </text>
            {a.extent && (
              <text x={ox + CELL_W - 14} y={oy + 38} fontSize="9.5" fontWeight="600" fill="#94a3b8" textAnchor="end">
                {formatChainage(a.extent.start)} → {formatChainage(a.extent.end)}
              </text>
            )}

            {/* Y reference lines: 0, 50%, 100% */}
            {[0, 0.5, 1].map((f) => {
              const y = oy + CELL_PAD.top + innerH - f * innerH;
              return (
                <g key={f}>
                  <line x1={ox + CELL_PAD.left} y1={y} x2={ox + CELL_W - CELL_PAD.right} y2={y} stroke="#eef2f7" strokeWidth="1" />
                  <text x={ox + CELL_PAD.left - 6} y={y + 3.5} fontSize="9" fill="#94a3b8" textAnchor="end" fontWeight="600">
                    {Math.round(maxQ * f).toLocaleString("en-ZA")}
                  </text>
                </g>
              );
            })}

            {/* Bars: one per production day */}
            {a.points.map((p) => {
              const h = (p.qty / maxQ) * innerH;
              const x = xFor(p.date);
              const y = oy + CELL_PAD.top + innerH - h;
              return (
                <rect
                  key={p.date}
                  x={x - barW / 2} y={y}
                  width={barW} height={Math.max(h, 1.5)}
                  fill={a.colour} opacity="0.85" rx="1.5"
                >
                  <title>{`${p.date}: ${Math.round(p.qty)} ${data.unit}`}</title>
                </rect>
              );
            })}

            {/* X axis: first / mid / last date */}
            {[0, Math.floor((n - 1) / 2), n - 1]
              .filter((v, idx, arr) => arr.indexOf(v) === idx)
              .map((di) => {
                const d = data.dateDomain[di];
                if (!d) return null;
                const x = xFor(d);
                return (
                  <text key={d} x={x} y={oy + CELL_H - 10} fontSize="9.5" fill="#64748b" textAnchor="middle" fontWeight="600">
                    {formatShortDate(d)}
                  </text>
                );
              })}
            <line
              x1={ox + CELL_PAD.left} y1={oy + CELL_PAD.top + innerH}
              x2={ox + CELL_W - CELL_PAD.right} y2={oy + CELL_PAD.top + innerH}
              stroke="#cbd5e1" strokeWidth="1"
            />
          </g>
        );
      })}
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Shared bits                                                          */
/* ------------------------------------------------------------------ */

function XAxisLabels({ dateDomain, xFor, baseY }) {
  const n = dateDomain.length;
  if (n === 0) return null;
  const step = Math.max(1, Math.ceil(n / 9));
  return (
    <g>
      {dateDomain
        .filter((_, i) => i % step === 0 || i === n - 1)
        .map((d) => (
          <g key={d}>
            <line x1={xFor(d)} y1={baseY} x2={xFor(d)} y2={baseY + 4} stroke="#64748b" strokeWidth="1" />
            <text x={xFor(d)} y={baseY + 18} fontSize="10" fill="#64748b" textAnchor="middle" fontWeight="600">
              {formatShortDate(d)}
            </text>
          </g>
        ))}
      <line x1={CPAD.left} y1={baseY} x2={CW - CPAD.right} y2={baseY} stroke="#0f172a" strokeWidth="1.2" />
    </g>
  );
}

function Stat({ label, value, helper }) {
  return (
    <div style={styles.stat}>
      <span style={styles.statLabel}>{label}</span>
      <strong style={styles.statValue}>{value}</strong>
      {helper ? <span style={styles.statHelper}>{helper}</span> : null}
    </div>
  );
}

function formatShortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-ZA", { month: "short", day: "numeric" });
}

function formatLongDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-ZA", { weekday: "short", year: "numeric", month: "short", day: "numeric" });
}

const ACTIVITY_PALETTE = [
  "#0ea5e9", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444",
  "#06b6d4", "#84cc16", "#f97316", "#ec4899", "#6366f1",
  "#14b8a6", "#eab308", "#a855f7",
];

/* ------------------------------------------------------------------ */
/* Styles                                                               */
/* ------------------------------------------------------------------ */

const styles = {
  wrapper: { display: "grid", gap: 14 },

  periodBar: {
    display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
    padding: "10px 12px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
  },
  periodLabel: {
    fontSize: 11, fontWeight: 800, color: "#64748b",
    textTransform: "uppercase", letterSpacing: 0.5,
  },
  select: {
    border: "1px solid #cbd5e1", background: "#ffffff",
    padding: "7px 10px", borderRadius: 9,
    fontSize: 12, fontWeight: 700, color: "#0f172a", cursor: "pointer",
  },
  stepper: { display: "inline-flex", gap: 4 },
  stepBtn: {
    border: "1px solid #cbd5e1", background: "#ffffff",
    width: 28, height: 28, borderRadius: 8,
    fontSize: 15, fontWeight: 800, color: "#0f172a", cursor: "pointer",
    lineHeight: 1,
  },
  dateInput: {
    border: "1px solid #cbd5e1", background: "#ffffff",
    padding: "6px 9px", borderRadius: 9,
    fontSize: 12, fontWeight: 600, color: "#0f172a",
  },
  rangeDash: { fontSize: 12, color: "#64748b", fontWeight: 800 },
  periodBadge: {
    marginLeft: "auto",
    background: "#0f172a", color: "#f8fafc",
    padding: "5px 11px", borderRadius: 999,
    fontSize: 11, fontWeight: 800,
  },
  clearBtn: {
    border: "1px solid #cbd5e1", background: "#ffffff",
    padding: "5px 10px", borderRadius: 999,
    fontSize: 11, fontWeight: 700, color: "#475569", cursor: "pointer",
  },

  summaryCard: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    padding: 14,
    display: "grid",
    gap: 10,
  },
  summaryHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" },
  summaryTitle: { margin: 0, fontSize: 13, fontWeight: 800, color: "#0f172a" },
  csvButton: {
    border: "1px solid #cbd5e1", background: "#ffffff",
    padding: "6px 12px", borderRadius: 10,
    fontSize: 11.5, fontWeight: 700, color: "#0f172a", cursor: "pointer",
  },
  summaryHeadline: { margin: 0, fontSize: 13, color: "#334155", lineHeight: 1.6 },
  tableScroll: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  th: {
    textAlign: "left", padding: "8px 10px",
    fontSize: 10.5, fontWeight: 800, color: "#64748b",
    textTransform: "uppercase", letterSpacing: 0.4,
    borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap",
  },
  td: {
    padding: "9px 10px", borderBottom: "1px solid #f1f5f9",
    color: "#0f172a", verticalAlign: "top",
  },
  tfootTd: {
    padding: "9px 10px", borderTop: "2px solid #e2e8f0",
    fontWeight: 800, color: "#0f172a",
  },
  chainageCell: { display: "block", fontWeight: 700, whiteSpace: "nowrap" },
  cellHelper: { display: "block", fontSize: 10.5, color: "#94a3b8", fontWeight: 600, marginTop: 2 },
  runWrap: { display: "flex", flexWrap: "wrap", gap: 5, maxWidth: 320 },
  runChip: {
    display: "inline-flex", alignItems: "center", gap: 5,
    border: "1px solid", borderRadius: 999,
    padding: "3px 9px", fontSize: 10.5, fontWeight: 700,
    color: "#0f172a", background: "#ffffff", whiteSpace: "nowrap",
  },
  runLen: { fontStyle: "normal", color: "#64748b", fontWeight: 600 },

  toolbar: {
    display: "flex", justifyContent: "space-between",
    alignItems: "center", gap: 12, flexWrap: "wrap",
  },
  chartTabs: { display: "inline-flex", gap: 4, background: "#f1f5f9", padding: 4, borderRadius: 12 },
  tab: {
    border: "none", background: "transparent",
    padding: "8px 14px", borderRadius: 9,
    fontSize: 12, fontWeight: 700, color: "#475569",
    cursor: "pointer",
  },
  tabActive: {
    background: "#0f172a", color: "#ffffff",
    boxShadow: "0 2px 6px rgba(15,23,42,0.3)",
  },
  tabCount: {
    marginLeft: 6, background: "#e0f2fe", color: "#0369a1",
    borderRadius: 999, padding: "1px 7px", fontSize: 10, fontWeight: 800,
  },

  /* ── Measurement sheet ── */
  sheet: { display: "grid", gap: 14 },
  sheetTotals: { display: "flex", flexWrap: "wrap", gap: 10 },
  sheetTotalCard: {
    display: "grid", gap: 1, padding: "10px 16px", borderRadius: 12,
    background: "#0f172a", minWidth: 130,
  },
  sheetTotalLabel: {
    fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
    textTransform: "uppercase", color: "#94a3b8",
  },
  sheetTotalValue: { fontSize: 22, fontWeight: 800, color: "#f8fafc", lineHeight: 1.15 },
  sheetTotalSub: { fontSize: 11, fontWeight: 700, color: "#64748b" },
  sheetGroupRow: {
    padding: "8px 12px", background: "#f1f5f9", color: "#475569",
    fontSize: 10, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase",
    borderBottom: "1px solid #e2e8f0",
  },
  sheetRow: { cursor: "pointer" },
  sheetCaret: { display: "inline-block", width: 16, color: "#94a3b8", fontSize: 11 },
  sheetActivityChip: {
    display: "inline-block", fontSize: 10, fontWeight: 700, color: "#475569",
    background: "#f8fafc", border: "1px solid #e2e8f0",
    borderRadius: 999, padding: "2px 8px",
  },
  sheetDetailCell: { padding: 0, background: "#f8fafc", borderBottom: "1px solid #e2e8f0" },
  sheetDetailTable: { width: "100%", borderCollapse: "collapse", fontSize: 11 },
  sheetDetailTh: {
    textAlign: "left", padding: "6px 12px", color: "#94a3b8",
    fontSize: 9, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase",
    borderBottom: "1px solid #e2e8f0",
  },
  sheetDetailTd: {
    padding: "6px 12px", color: "#334155",
    borderBottom: "1px solid #eef2f7",
    fontFamily: "ui-monospace, SFMono-Regular, monospace",
  },
  sheetFoot: { margin: 0, fontSize: 11, color: "#94a3b8", fontWeight: 600 },
  exportButton: {
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    padding: "8px 14px",
    borderRadius: 10,
    fontSize: 12, fontWeight: 700,
    color: "#0f172a",
    cursor: "pointer",
  },
  legendRow: { display: "flex", gap: 8, flexWrap: "wrap" },
  legendChip: {
    display: "inline-flex", alignItems: "center", gap: 7,
    border: "1.5px solid", background: "#ffffff",
    padding: "5px 11px", borderRadius: 999,
    fontSize: 12, fontWeight: 700, color: "#0f172a",
    cursor: "pointer",
  },
  legendDot: { width: 9, height: 9, borderRadius: "50%", display: "inline-block", flexShrink: 0 },
  legendTotal: { fontSize: 11, fontWeight: 700, color: "#64748b" },
  chartHost: {
    width: "100%",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    padding: 8,
    overflow: "hidden",
  },
  tooltip: {
    position: "absolute",
    top: 50,
    background: "#0f172a",
    color: "#f8fafc",
    borderRadius: 10,
    padding: "10px 12px",
    fontSize: 12,
    display: "grid",
    gap: 5,
    minWidth: 190,
    pointerEvents: "none",
    boxShadow: "0 12px 30px rgba(15,23,42,0.35)",
    zIndex: 10,
  },
  tooltipDate: { fontWeight: 800, fontSize: 12, marginBottom: 2, color: "#cbd5e1" },
  tooltipRow: { display: "flex", alignItems: "center", gap: 7 },
  emptyCard: {
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
    color: "#94a3b8", fontSize: 13,
    padding: 50,
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
  },
  emptyInline: {
    padding: 40, textAlign: "center",
    color: "#94a3b8", fontSize: 13, fontWeight: 600,
  },

  footerStats: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: 10,
    padding: 14,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
  },
  stat: { display: "grid", gap: 2 },
  statLabel: { fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 },
  statValue: { fontSize: 18, fontWeight: 800, color: "#0f172a" },
  statHelper: { fontSize: 11, color: "#94a3b8", fontWeight: 600 },
};
